// Revenue across all gateways: daily stacked chart + top packs + top customers.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { Loader2 } from "lucide-react";

type PaddleOrder = { amount_cents: number; pack_id: string; user_id: string; created_at: string; currency: string };
type RazOrder = { amount_paise: number; credits: number; user_id: string; created_at: string };
type PlayOrder = { amount_micros: number | null; pack_id: string; user_id: string; created_at: string; currency: string };

const fmtUSD = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export default function AdminRevenue() {
  const [paddle, setPaddle] = useState<PaddleOrder[]>([]);
  const [raz, setRaz] = useState<RazOrder[]>([]);
  const [play, setPlay] = useState<PlayOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [p, r, pl] = await Promise.all([
        supabase
          .from("paddle_orders")
          .select("amount_cents,pack_id,user_id,created_at,currency")
          .eq("status", "paid")
          .gte("created_at", since)
          .limit(10000),
        supabase
          .from("razorpay_orders")
          .select("amount_paise,credits,user_id,created_at")
          .eq("status", "paid")
          .gte("created_at", since)
          .limit(10000),
        supabase
          .from("play_orders")
          .select("amount_micros,pack_id,user_id,created_at,currency")
          .eq("status", "paid")
          .gte("created_at", since)
          .limit(10000),
      ]);
      if (!active) return;
      setPaddle((p.data ?? []) as PaddleOrder[]);
      setRaz((r.data ?? []) as RazOrder[]);
      setPlay((pl.data ?? []) as PlayOrder[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const series = useMemo(() => {
    const map = new Map<string, { day: string; paddle: number; razorpay: number; play: number }>();
    const ensure = (day: string) => {
      if (!map.has(day)) map.set(day, { day, paddle: 0, razorpay: 0, play: 0 });
      return map.get(day)!;
    };
    for (const o of paddle) ensure(o.created_at.slice(0, 10)).paddle += o.amount_cents / 100;
    // Razorpay paise → assume ~83 INR/USD for normalization display.
    for (const o of raz) ensure(o.created_at.slice(0, 10)).razorpay += (o.amount_paise / 100) / 83;
    for (const o of play) ensure(o.created_at.slice(0, 10)).play += Number(o.amount_micros ?? 0) / 1_000_000;
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [paddle, raz, play]);

  const totals = useMemo(() => {
    const paddleTotal = paddle.reduce((s, o) => s + o.amount_cents / 100, 0);
    const razTotal = raz.reduce((s, o) => s + o.amount_paise / 100 / 83, 0);
    const playTotal = play.reduce((s, o) => s + Number(o.amount_micros ?? 0) / 1_000_000, 0);
    return { paddleTotal, razTotal, playTotal, grand: paddleTotal + razTotal + playTotal };
  }, [paddle, raz, play]);

  const topPacks = useMemo(() => {
    const m = new Map<string, { pack: string; count: number; revenue: number }>();
    for (const o of paddle) {
      const cur = m.get(o.pack_id) ?? { pack: o.pack_id, count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += o.amount_cents / 100;
      m.set(o.pack_id, cur);
    }
    for (const o of play) {
      const cur = m.get(o.pack_id) ?? { pack: o.pack_id, count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += Number(o.amount_micros ?? 0) / 1_000_000;
      m.set(o.pack_id, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [paddle, play]);

  const topCustomers = useMemo(() => {
    const m = new Map<string, { user_id: string; orders: number; revenue: number }>();
    const bump = (uid: string, rev: number) => {
      const cur = m.get(uid) ?? { user_id: uid, orders: 0, revenue: 0 };
      cur.orders += 1;
      cur.revenue += rev;
      m.set(uid, cur);
    };
    for (const o of paddle) bump(o.user_id, o.amount_cents / 100);
    for (const o of raz) bump(o.user_id, o.amount_paise / 100 / 83);
    for (const o of play) bump(o.user_id, Number(o.amount_micros ?? 0) / 1_000_000);
    return Array.from(m.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 20);
  }, [paddle, raz, play]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Revenue</h1>
        <p className="text-sm text-muted-foreground">Last 30 days, normalized to USD.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total (30d)" value={fmtUSD(totals.grand)} />
        <Kpi label="Paddle" value={fmtUSD(totals.paddleTotal)} />
        <Kpi label="Razorpay (≈USD)" value={fmtUSD(totals.razTotal)} />
        <Kpi label="Play Store" value={fmtUSD(totals.playTotal)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily revenue by gateway</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="paddle" stackId="a" fill="hsl(var(--primary))" />
                <Bar dataKey="razorpay" stackId="a" fill="hsl(var(--chart-2, 199 89% 48%))" />
                <Bar dataKey="play" stackId="a" fill="hsl(var(--chart-3, 142 71% 45%))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top packs</CardTitle>
          </CardHeader>
          <CardContent>
            {topPacks.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No sales yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="pb-2">Pack</th>
                    <th className="pb-2 text-right">Orders</th>
                    <th className="pb-2 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topPacks.map((p) => (
                    <tr key={p.pack} className="border-t">
                      <td className="py-2 font-mono text-[11px]">{p.pack}</td>
                      <td className="py-2 text-right tabular-nums">{p.count}</td>
                      <td className="py-2 text-right tabular-nums">{fmtUSD(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top customers</CardTitle>
          </CardHeader>
          <CardContent>
            {topCustomers.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No sales yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="pb-2">User</th>
                    <th className="pb-2 text-right">Orders</th>
                    <th className="pb-2 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((c) => (
                    <tr key={c.user_id} className="border-t">
                      <td className="py-2 font-mono text-[10px]">{c.user_id.slice(0, 18)}…</td>
                      <td className="py-2 text-right tabular-nums">{c.orders}</td>
                      <td className="py-2 text-right tabular-nums">{fmtUSD(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
